<template>
  <div ref="printHost" class="hajj-garage-transfer-print-root">
    <div
      v-show="showPrintSheet"
      ref="printSheetEl"
      class="hajj-transfer-print-sheet"
      aria-hidden="true"
    >
      <div v-for="copy in [1, 2, 3]" :key="copy" class="hajj-transfer-copy hajj-transfer-copy--print">
        <h2 class="hajj-transfer-print-title">{{ $t("hajj-garage-transfer-print-title") }}</h2>
        <div class="hajj-transfer-copy__meta">
          <div>{{ $t("copy") }}: {{ copy }}/3</div>
          <div>{{ $t("start-time") }}: {{ formattedStartTime }}</div>
          <div>{{ $t("hajj-garage-transfer-from-garage") }}: {{ $t("hajj-operations-yard") }}</div>
          <div>{{ $t("hajj-garage-transfer-to-garage") }}: {{ displayDestination }}</div>
          <div>{{ $t("service") }}: {{ displayService }}</div>
        </div>
        <div class="hajj-transfer-copy__grid">
          <div>
            <div class="hajj-transfer-copy__label mb-1">{{ $t("driver") }}</div>
            <div class="hajj-transfer-copy__line">{{ displayDriverLine }}</div>
            <div class="hajj-transfer-copy__sub">{{ $t("employee-id") }}: {{ activePayload.driverEmployeeId || "—" }}</div>
            <div class="hajj-transfer-copy__sub">{{ $t("driver-mobile-no") }}: {{ activePayload.driverMobileNo || "—" }}</div>
            <base-generate-qr class="hajj-transfer-copy__qr" :value="driverQrValue" :size="220" />
          </div>
          <div>
            <div class="hajj-transfer-copy__label mb-1">{{ $t("vehicle") }}</div>
            <div class="hajj-transfer-copy__line">{{ displayVehicleLine }}</div>
            <div class="hajj-transfer-copy__sub">{{ $t("operational-id") }}: {{ activePayload.vehicleOperationalId || "—" }}</div>
            <div class="hajj-transfer-copy__sub">{{ $t("vehicle-type") }}: {{ activePayload.vehicleTypeText || "—" }}</div>
            <base-generate-qr class="hajj-transfer-copy__qr" :value="vehicleQrValue" :size="220" />
          </div>
        </div>
        <div v-if="activePayload.showReassignNote" class="hajj-transfer-copy__reassign-note">
          {{ $t("hajj-garage-transfer-reassignment-note") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

const defaultPayload = () => ({
  startTime: null,
  destinationText: "",
  serviceText: "",
  driverLine: "",
  driverEmployeeId: "",
  driverMobileNo: "",
  vehicleLine: "",
  vehicleOperationalId: "",
  vehicleTypeText: "",
  showReassignNote: false,
});

export default {
  name: "HajjGarageTransferOrderPrintSheet",
  props: {
    payload: {
      type: Object,
      default: () => defaultPayload(),
    },
  },
  data() {
    return {
      showPrintSheet: false,
      printPayloadOverride: null,
    };
  },
  computed: {
    activePayload() {
      if (this.printPayloadOverride && typeof this.printPayloadOverride === "object") {
        return { ...defaultPayload(), ...this.printPayloadOverride };
      }
      return { ...defaultPayload(), ...(this.payload || {}) };
    },
    displayDestination() {
      return this.activePayload?.destinationText || "—";
    },
    displayService() {
      return this.activePayload?.serviceText || "—";
    },
    displayDriverLine() {
      return this.activePayload?.driverLine || "—";
    },
    displayVehicleLine() {
      return this.activePayload?.vehicleLine || "—";
    },
    formattedStartTime() {
      const v = this.activePayload?.startTime;
      if (!v) return "—";
      return moment(v).locale("en").format("YYYY-MM-DD HH:mm");
    },
    driverQrValue() {
      return JSON.stringify({ employeeID: this.activePayload?.driverEmployeeId || "" });
    },
    vehicleQrValue() {
      return JSON.stringify({ operationalID: this.activePayload?.vehicleOperationalId || "" });
    },
  },
  methods: {
    /**
     * Append the sheet to document.body during print so page-break / one-copy-per-page
     * matches the Hajj Resource Transfer submit flow (not clipped by sidebar/transforms).
     */
    async runPrint(overridePayload = null) {
      this.printPayloadOverride =
        overridePayload && typeof overridePayload === "object"
          ? { ...defaultPayload(), ...overridePayload }
          : null;
      this.showPrintSheet = true;
      await this.$nextTick();
      const host = this.$refs.printHost;
      const sheet = this.$refs.printSheetEl;
      if (!host || !sheet) {
        this.showPrintSheet = false;
        return;
      }
      let movedToBody = false;
      try {
        document.body.appendChild(sheet);
        movedToBody = sheet.parentNode === document.body;
        document.body.classList.add("hajj-transfer-print-mode");
        await this.$nextTick();
        await new Promise((r) => requestAnimationFrame(() => r()));
        window.print();
      } finally {
        document.body.classList.remove("hajj-transfer-print-mode");
        if (movedToBody && host && sheet) {
          try {
            host.appendChild(sheet);
          } catch (_) {
            /* ignore */
          }
        }
        this.showPrintSheet = false;
        this.printPayloadOverride = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.hajj-garage-transfer-print-root {
  position: static;
}

.hajj-transfer-copy {
  padding: 16px;
}

.hajj-transfer-copy--print .hajj-transfer-print-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.hajj-transfer-copy--print .hajj-transfer-copy__meta {
  font-size: 1rem;
  line-height: 1.5;
}

.hajj-transfer-copy--print .hajj-transfer-copy__label {
  font-size: 1.05rem;
  font-weight: 700;
}

.hajj-transfer-copy--print .hajj-transfer-copy__line {
  font-size: 1.05rem;
}

.hajj-transfer-copy--print .hajj-transfer-copy__sub {
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  opacity: 0.9;
}

.hajj-transfer-copy__meta {
  margin-bottom: 12px;
}

.hajj-transfer-copy__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

.hajj-transfer-copy__reassign-note {
  margin-top: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  text-align: center;
}
</style>

<style lang="scss">
body.hajj-transfer-print-mode * {
  visibility: hidden;
}

body.hajj-transfer-print-mode .hajj-transfer-print-sheet,
body.hajj-transfer-print-mode .hajj-transfer-print-sheet * {
  visibility: visible;
}

body.hajj-transfer-print-mode .hajj-transfer-print-sheet {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  background: #fff;
  display: block !important;
}

/* While printing from trip drawer/add page, sheet is moved under body — full-width flow for page breaks */
body > .hajj-transfer-print-sheet {
  position: relative !important;
  left: auto !important;
  top: auto !important;
}

@media print {
  body.hajj-transfer-print-mode .sidebar,
  body.hajj-transfer-print-mode .header {
    display: none !important;
  }
  body.hajj-transfer-print-mode .layout {
    width: 100% !important;
  }
  body.hajj-transfer-print-mode .modal,
  body.hajj-transfer-print-mode .modal-backdrop {
    display: none !important;
  }
  body.hajj-transfer-print-mode .hajj-garage-transfer-add > *:not(.hajj-garage-transfer-print-root) {
    display: none !important;
  }
  body.hajj-transfer-print-mode .trip-detail-hide-on-transfer-print {
    display: none !important;
  }
  body.hajj-transfer-print-mode aside,
  body.hajj-transfer-print-mode #trip-detail-sidebar-backdrop,
  body.hajj-transfer-print-mode .b-sidebar,
  body.hajj-transfer-print-mode .modal-backdrop {
    display: none !important;
  }
  body.hajj-transfer-print-mode .hajj-transfer-print-sheet {
    position: static !important;
    left: auto !important;
    top: auto !important;
    display: block !important;
    width: 100% !important;
    max-width: none !important;
    background: #fff;
  }
  .hajj-transfer-copy {
    box-sizing: border-box;
    padding: 12px 0;
    page-break-inside: avoid;
    break-inside: avoid;
    font-size: 12pt;
    min-height: 0;
  }
  .hajj-transfer-copy--print .hajj-transfer-print-title {
    font-size: 18pt;
  }
  .hajj-transfer-copy--print .hajj-transfer-copy__meta,
  .hajj-transfer-copy--print .hajj-transfer-copy__line {
    font-size: 12pt;
  }
  .hajj-transfer-copy--print .hajj-transfer-copy__label {
    font-size: 13pt;
  }
  .hajj-transfer-copy--print .hajj-transfer-copy__sub {
    font-size: 11pt;
  }
  .hajj-transfer-copy:not(:last-of-type) {
    page-break-after: always !important;
    break-after: page !important;
  }
  @page {
    size: A4;
    margin: 12mm;
  }
}
</style>
